import { inject, Injectable } from "@angular/core";
import { BehaviorSubject, defer, Observable } from "rxjs";

import { LogService } from "@bitwarden/common/abstractions/log.service";
import { Transaction } from "@bitwarden/web-vault/app/models/data/blobby/transaction.data";
import { BalanceForm, TransactionForm } from "@bitwarden/web-vault/app/models/types/account.types";
import { transactionDataType } from "@bitwarden/web-vault/app/models/types/transactionData.types";
import { MarketDataService } from "@bitwarden/web-vault/app/services/DataService/market-data/market-data.service";
import { DataRepositoryService } from "../../DataRepository/data-repository.service";
import { DataServiceAbstraction } from "../data.service.abstraction";
import {
  AccountView,
  isStockOrOther,
} from "@bitwarden/web-vault/app/models/view/account/account.view";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import {
  createTransactionView,
  newAllocationWithNewCategory,
} from "@bitwarden/web-vault/app/models/view/transaction/transaction.utils";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import {
  isCurrency,
  isReadOnlySymbol,
} from "@bitwarden/web-vault/app/models/view/symbol/currency-utility";
import { AllocationUtils } from "@bitwarden/web-vault/app/models/store/allocations/allocation.utils";
import { ALIGNMENT_BALANCE_RESET } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { RoleAccessService } from "@bitwarden/web-vault/app/services/permission/role-access.service";

@Injectable({
  providedIn: "root",
})
export class TransactionService implements DataServiceAbstraction {
  private savingItemAccounts = new BehaviorSubject<number>(0);
  savingItemAccounts$ = this.savingItemAccounts.asObservable();

  private totalAccountsToProcess = new BehaviorSubject<number>(0);
  totalAccountsToProcess$ = this.totalAccountsToProcess.asObservable();

  private transactionStore = inject(TransactionStoreService);
  private roleAccessService = inject(RoleAccessService);

  constructor(
    private dataRepositoryService: DataRepositoryService,
    private logService: LogService,
  ) {}

  /**
   * @deprecated
   */
  async getAll(runValuation = true) {
    return await this.dataRepositoryService.getAllTransactions(runValuation);
  }

  getAccountTransactions(account: AccountView) {
    return this.transactionStore.getAccountTransaction(account);
  }

  /**
   * For stock/other accounts, the symbol the account most recently transacted in — used to
   * default Add Balance and Add Transaction to the same symbol instead of disagreeing.
   */
  getMostRecentSymbolForAccount(account: AccountView): string | undefined {
    if (!isStockOrOther(account.type)) {
      return undefined;
    }
    return this.getAccountTransactions(account)
      .filter((t) => t.quantity?.actualQuantity?.symbol)
      .sort((a, b) => b.postDate.getTime() - a.postDate.getTime())[0]?.quantity.actualQuantity
      .symbol;
  }

  /**
   * The full set of symbols Add Balance's currency dropdown offers (non-currency generated
   * symbols plus the tenant's forex currencies). Shared by AccountBalanceComponent.symbolOptions
   * and DialogFactory so the two never drift apart.
   */
  getSelectableBalanceSymbols(): string[] {
    const nonCreditSymbols = this.transactionStore.generatedSymbols
      .collection()
      .filter((s) => !isReadOnlySymbol(s.code))
      .map((s) => s.code);
    const currencies =
      (this.roleAccessService.getRoleAccess().getClaim().forex_currency as string[]) ?? [];
    return nonCreditSymbols.concat(currencies);
  }

  getAllTransactions() {
    return this.transactionStore.enrichTransaction.collection();
  }

  startAccountImport() {
    this.savingItemAccounts = new BehaviorSubject<number>(0);
    this.savingItemAccounts$ = this.savingItemAccounts.asObservable();
  }

  completeAccountProgress() {
    this.savingItemAccounts.complete();
  }

  /**
   * @deprecated
   */
  async getAllSourceTransactions() {
    return await this.dataRepositoryService.getAllSourceTransactions();
  }

  /**
   * @deprecated
   */
  //todo implement
  async create(transaction: Transaction): Promise<Transaction> {
    return Promise.resolve(undefined);
  }

  /**
   * @deprecated
   */
  async delete(transaction: Transaction): Promise<boolean> {
    return await this.dataRepositoryService.deleteTransaction(transaction);
  }

  async get(itemId: string): Promise<Transaction> {
    const transaction = await this.getAll(false);
    return transaction.find((t) => t.id === itemId);
  }

  /**
   * @deprecated
   */
  async update(transaction: Transaction, triggerObservable?: boolean): Promise<Transaction> {
    try {
      return await this.dataRepositoryService.updateTransaction(transaction);
    } catch (e) {
      this.logService.error(e);
      throw e;
    }
  }

  /**
   * @deprecated
   */
  async import(transactions: Transaction[]): Promise<boolean> {
    MarketDataService.refreshTransactions = true;
    return await this.dataRepositoryService.importTransactions(transactions);
  }

  /**
   * Create a balance alignment transaction record from a balance form,
   * for an account using the createTransactionView function. Saves this
   * transaction view to DALI and reloads it into the transaction store.
   */
  async createAccountAlignmentTransaction(
    account: AccountView,
    form: BalanceForm,
  ): Promise<TransactionView> {
    const description = form.description ? form.description : ALIGNMENT_BALANCE_RESET;

    let date: Date;
    if (form.time) {
      date = new Date(`${form.date}T${form.time}`);
    } else {
      date = new Date(form.date);
    }

    let currency = form.currency;
    if (!isCurrency(form.currency)) {
      currency = account.currency;
    }

    const transactionView = createTransactionView(
      account.id,
      account.institutionLink.institutionId,
      0,
      form.currency, // symbol
      date,
      description,
      [], // allocations (TODO: calculate with account default when it is implemented)
      [], // linkedTo
      true,
      form.transactionStatus as TransactionStatusEnum,
      Number(form.balance), // bankImportedBalance
      currency,
    );

    await this.transactionStore.transaction.save(transactionView);
    return transactionView;
  }

  async createNormalTransactions(transactions: transactionDataType[]): Promise<TransactionView[]> {
    const transactionViews = transactions.map((transaction) =>
      this.buildNormalTransactionView(transaction.account, transaction.formData),
    );
    if (transactionViews.length === 0) {
      return [];
    }
    await this.transactionStore.transaction.saveToVault(transactionViews, true);
    return transactionViews;
  }

  private buildNormalTransactionView(account: AccountView, form: TransactionForm): TransactionView {
    let date: Date;
    if (form.time) {
      date = new Date(`${form.date}T${form.time}`);
    } else {
      date = new Date(form.date);
    }

    let amount = 0;
    if (form.type === "OUT") {
      amount = -Math.abs(form.amount);
    } else {
      amount = Math.abs(form.amount);
    }

    let currency = form.currency;
    if (!isCurrency(form.currency)) {
      currency = account.currency;
    }

    return createTransactionView(
      account.id,
      account.institutionLink.institutionId,
      amount,
      form.currency,
      date,
      form.description || `Fake Transaction (${form.transactionStatus}) ${form.amount}`,
      form.categoryId
        ? newAllocationWithNewCategory(form.categoryId)
        : AllocationUtils.createDefaultAllocations(), // for now we will assign the default allocation of Me and choice of category from user  (TODO: calculate with account default when it is implemented)
      [], // linkedTo
      true,
      form.transactionStatus as TransactionStatusEnum,
      null,
      currency,
    );
  }
}
